/**
 * Operations analytics (`ops.*`): rep activity, lead funnel, bonus simulator, exports, admin live map.
 * Merged into the dictionary through `extra.ts`; `opsEn` is typed against `opsPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const opsPl = {
  nav: {
    activity: 'Aktywność handlowców',
    funnel: 'Lejek pozyskania',
    bonus: 'Premie',
    live: 'Mapa na żywo',
  },
  activity: {
    subtitle: 'Wizyty, pokrycie portfela regułami kontaktu, dyscyplina GPS i anomalie.',
    kpi: {
      section: 'Wskaźniki aktywności',
      visits: 'Wizyty zrealizowane',
      coverage: 'Pokrycie portfela',
      avgTime: 'Średni czas wizyty',
      perDay: '{days} dni roboczych w okresie',
      gps: 'Wizyty z GPS',
      gpsHint: 'Udział wizyt rozpoczętych z lokalizacją',
      tasks: 'Zadania wykonane',
      anomalies: 'Anomalie',
      anomaliesHint: 'Do weryfikacji przez kierownika',
    },
    table: {
      title: 'Aktywność handlowców',
      subtitle: 'Kliknij nagłówek, aby posortować',
      rep: 'Handlowiec',
      visits: 'Wizyty',
      visitsHint: 'Zrealizowane, nieodbyte i zaplanowane wizyty w okresie',
      missed: 'nieodb.',
      planned: 'plan.',
      perDay: 'Wiz./dzień rob.',
      coverage: 'Pokrycie portfela',
      avgTime: 'Śr. czas',
      gps: 'GPS vs ręcznie',
      gpsHint: '{gps} wizyt z GPS, {manual} wpisanych ręcznie',
      sales: 'Sprzedaż',
      tasks: 'Zadania',
      overdue: 'po terminie',
      anomalies: 'Anomalie',
    },
    coverage: {
      title: 'Pokrycie portfela kontaktem',
      subtitle: 'Klienci obsłużeni zgodnie z kadencją kontaktu (reguły wizyt)',
      onTime: 'Kontakt w kadencji',
      late: 'Po terminie kontaktu',
      clients: 'klientów',
    },
    scatter: {
      title: 'Wizyty a sprzedaż',
      subtitle: 'Każdy punkt to handlowiec w wybranym okresie',
      visits: 'Wizyty zrealizowane',
      sales: 'Sprzedaż',
      trend: 'Trend',
    },
    corr: {
      strongPos: 'silna dodatnia zależność: więcej wizyt idzie w parze z wyższą sprzedażą.',
      moderatePos: 'umiarkowana dodatnia zależność: wizyty częściowo tłumaczą wynik sprzedaży.',
      weakPos: 'słaba dodatnia zależność.',
      none: 'brak wyraźnej zależności między liczbą wizyt a sprzedażą.',
      weakNeg: 'słaba ujemna zależność.',
      moderateNeg: 'umiarkowana ujemna zależność: więcej wizyt nie przekłada się na sprzedaż.',
      strongNeg: 'silna ujemna zależność: warto sprawdzić jakość wizyt i portfel.',
      unavailable: 'Za mało danych, aby policzyć korelację (potrzeba co najmniej 3 handlowców).',
      caveat: 'Korelacja nie dowodzi przyczyny — portfele handlowców różnią się wielkością.',
    },
    anomalies: {
      title: 'Anomalie w pracy w terenie',
      subtitle: 'Wizyty i sygnał GPS wymagające sprawdzenia',
      none: 'Brak anomalii w wybranym okresie',
      more: 'Pokaż więcej ({count})',
      less: 'Pokaż mniej',
    },
    anomalyType: {
      GEOFENCE: 'Poza strefą klienta',
      SHORT_VISIT: 'Zbyt krótka wizyta',
      NIGHT_VISIT: 'Wizyta w nocy',
      GPS_GAP: 'Luka w sygnale GPS',
    },
    anomaly: {
      GEOFENCE: 'Wizyta rozpoczęta {value} m od klienta (limit {limit} m).',
      SHORT_VISIT: 'Wizyta trwała {value} min (minimum {limit} min).',
      NIGHT_VISIT: 'Wizyta rozpoczęta o {value}:00 (poza godzinami {to}:00–{from}:00).',
      GPS_GAP: 'Brak sygnału GPS przez {value} min (limit {limit} min).',
    },
    severity: { high: 'Wysoka', medium: 'Średnia', low: 'Niska' },
  },
  funnel: {
    subtitle: 'Droga leadów od pierwszego kontaktu do pierwszego zamówienia: konwersja, czas i skuteczność źródeł.',
    period: { all: 'Wszystkie leady', l12: '12 miesięcy', ytd: 'Od początku roku', quarter: 'Kwartał' },
    source: 'Źródło',
    allSources: 'Wszystkie źródła',
    noSource: 'Brak źródła',
    empty: 'Brak leadów dla wybranych filtrów',
    days: '{value} dni',
    kpi: {
      section: 'Wskaźniki lejka',
      total: 'Leady w lejku',
      openHint: 'Otwarte: {count}',
      openPotential: 'Potencjał otwartych',
      openPotentialHint: 'Suma potencjału leadów w toku',
      winRate: 'Skuteczność (win rate)',
      winRateHint: 'Wygrane: {won} · utracone: {lost}',
      daysToWin: 'Czas do wygranej',
      daysToWinHint: 'Średnio od utworzenia leada',
      daysToLossHint: 'Utrata po średnio {value} dni',
      firstOrder: 'Lead → 1. zamówienie',
      firstOrderHint: 'Z zamówieniem: {withOrder} · skonwertowanych: {converted}',
      median: 'mediana {value} dni',
    },
    chart: {
      title: 'Lejek pozyskania',
      subtitle: 'Leady, które dotarły do danego etapu, i konwersja między etapami',
      stage: 'Etap',
      reached: 'Dotarło do etapu',
      avgDays: 'Śr. czas w etapie',
    },
    nowHere: 'Teraz: {count} · {potential}',
    conversionHint: 'Konwersja z poprzedniego etapu',
    avgDaysHint: 'Średni czas, jaki leady spędziły w bieżącym etapie',
    bySource: { title: 'Skuteczność źródeł', subtitle: 'Które kanały pozyskania dowożą klientów' },
    byRep: { title: 'Porównanie handlowców', subtitle: 'Leady i skuteczność wg opiekuna' },
    group: {
      leads: 'Leady',
      open: 'Otwarte',
      won: 'Wygrane',
      lost: 'Utracone',
      winRate: 'Win rate',
      wonPotential: 'Wygrany potencjał',
      daysToWin: 'Dni do wygranej',
      best: 'Najlepszy',
    },
  },
  bonus: {
    subtitleSim: 'Symulacja premii zespołu wg schematu: składniki, korekty i suma dla organizacji.',
    subtitleMine: 'Twoja przewidywana premia i to, co możesz zrobić, aby ją zwiększyć.',
    tab: { simulator: 'Symulator', mine: 'Moja premia' },
    period: 'Miesiąc',
    scheme: 'Schemat',
    schemeAuto: 'Schemat obowiązujący w okresie',
    active: 'aktywny',
    projection: 'Prognoza do końca miesiąca',
    projectionHint: 'Wyniki bieżącego miesiąca przeskalowane do pełnej liczby dni roboczych',
    projectionInfo: 'Prognoza: {done} z {total} dni roboczych (×{factor})',
    editScheme: 'Edytuj schemat',
    pcs: 'szt.',
    weight: 'waga {value}%',
    noTarget: 'brak celu',
    belowThreshold: 'poniżej progu',
    capped: 'limit {value}',
    thresholdMark: 'Próg: {value}%',
    targetMark: '100% celu',
    ofBase: '{value} bazy',
    kpi: {
      section: 'Podsumowanie premii',
      total: 'Suma premii',
      totalHint: 'Do wypłaty dla całego zespołu',
      avg: 'Średnia premia',
      avgHint: 'Na handlowca',
      base: 'Premia bazowa',
      baseHint: 'Pula przy 100%: {total}',
      reps: 'Handlowcy',
    },
    chart: { title: 'Z czego składa się premia', subtitle: 'Wkład każdego składnika w premię handlowca, w PLN' },
    component: { sales: 'Sprzedaż', margin: 'Marża', newCustomers: 'Nowi klienci', tasksVisits: 'Zadania i wizyty' },
    mandatory: {
      surveyCompleteness: 'Kompletność ankiet',
      visitsCount: 'Liczba wizyt',
      dataCompleteness: 'Kompletność danych klientów',
      reportPunctuality: 'Terminowość raportów',
    },
    table: {
      title: 'Premie wg handlowców',
      subtitle: 'Wynik składników (% celu) i kwota; rozwiń wiersz, aby zobaczyć wyjaśnienie',
      rep: 'Handlowiec',
      adjustments: 'Korekty obowiązkowe',
      premium: 'Premia',
      details: 'Szczegóły',
      explanation: 'Jak policzono',
      components: 'Składniki: wynik / cel',
      mandatory: 'Obowiązkowa aktywność',
    },
    mine: {
      predicted: 'Przewidywana premia',
      result: 'Premia za miesiąc',
      ofBase: 'Udział w premii bazowej',
      baseIs: '(baza {value})',
      adjustments: 'Korekty obowiązkowe',
      progress: 'Postęp składników',
      progressHint: 'Znacznik cienki: próg, gruby: 100% celu',
      raise: 'Co zrobić, żeby zarobić więcej',
      raiseHint: 'Posortowane od największego zysku',
      maxed: 'Wszystkie składniki są na maksimum, świetna robota!',
      how: 'Jak to liczymy',
      howHint: 'Schemat: {name}',
    },
    tip: {
      threshold: '{name}: brakuje {needed} do progu premiowania. Po jego przekroczeniu składnik zaczyna zarabiać.',
      full: '{name}: brakuje {needed} do pełnego celu.',
      cap: '{name}: jeszcze {needed} do limitu składnika.',
      mandatory: '{name}: brakuje {needed} do wymaganego poziomu. Unikasz kary i dostajesz bonus.',
    },
    edit: {
      title: 'Edycja schematu premiowego',
      editing: 'Zmiany zapisują schemat „{name}”',
      defaultScheme: 'Schemat domyślny. Zapisanie utworzy nowy schemat w bazie.',
      basic: 'Podstawowe',
      name: 'Nazwa schematu',
      validFrom: 'Obowiązuje od',
      validTo: 'Obowiązuje do',
      baseAmount: 'Premia bazowa (100% wyniku)',
      active: 'Schemat aktywny',
      weights: 'Wagi składników',
      weightsHint: 'Suma wag musi wynosić 100%.',
      weightsTotal: 'Suma wag: {sum}%',
      normalize: 'Wyrównaj do 100%',
      components: 'Progi i limity',
      componentsHint: 'Poniżej progu składnik = 0; wynik ponad limit jest ucinany.',
      threshold: 'Próg',
      thresholdHint: 'Wynik (% celu) poniżej progu daje 0 zł za składnik',
      cap: 'Limit',
      capHint: 'Maksymalny wynik składnika (% celu)',
      defaultMargin: 'Domyślny cel marży',
      newPerMonth: 'Nowi klienci / mies.',
      visitsPerMonth: 'Wizyt / mies.',
      taskOnTime: 'Zadania na czas',
      mandatory: 'Obowiązkowa aktywność',
      mandatoryHint: 'Spełnienie progu daje bonus, niespełnienie karę.',
      minimum: 'Minimum',
      bonus: 'Bonus',
      penalty: 'Kara',
      save: 'Zapisz zmiany',
      saveNew: 'Zapisz jako nowy',
      create: 'Utwórz schemat',
      errors: 'Błędów do poprawienia: {count}',
      unsaved: 'Niezapisane zmiany',
      noChanges: 'Brak zmian',
    },
    err: {
      name: 'Podaj nazwę schematu.',
      validFrom: 'Podaj datę początku.',
      validTo: 'Data końca nie może być wcześniejsza niż początku.',
      positive: 'Podaj liczbę większą od zera.',
      number: 'Podaj liczbę nieujemną.',
      percent: 'Podaj wartość od 0 do 100.',
      weightsSum: 'Wagi sumują się do {sum}%, a muszą do 100%.',
      threshold: 'Próg musi mieścić się w zakresie 0–100%.',
      cap: 'Limit musi mieścić się w zakresie 100–300%.',
    },
    copySuffix: 'kopia',
    saved: 'Schemat premiowy zapisany.',
    saveError: 'Nie udało się zapisać schematu.',
  },
  exports: {
    label: 'Eksportuj',
    excel: 'Excel (.xlsx)',
    pdf: 'PDF',
    preparing: 'Przygotowuję plik…',
    done: 'Pobrano plik {name}',
    error: 'Nie udało się wygenerować eksportu.',
    forbidden: 'Brak uprawnień do eksportu tych danych.',
    unavailable: 'Eksport nie jest jeszcze dostępny.',
    customersXlsx: 'Lista klientów (Excel)',
    customerPdf: 'Karta klienta (PDF)',
    boardPdf: 'Raport zarządu (PDF)',
    repsXlsx: 'Tabela handlowców (Excel)',
    drillXlsx: 'Lista klientów (Excel)',
    alertsXlsx: 'Alerty (Excel)',
    tasksXlsx: 'Zadania (Excel)',
  },
  live: {
    subtitle: 'Gdzie są handlowcy i kto jest teraz na wizycie. Odświeżanie co 30 s.',
    updated: 'Odświeżono {time}',
    refresh: 'Odśwież',
    reps: 'Handlowcy',
    onVisit: 'na wizycie',
    online: 'online',
    noGps: 'bez GPS',
    search: 'Szukaj handlowca',
    empty: 'Brak handlowców w zakresie',
    emptyHint: 'Gdy handlowcy zaczną pracę w aplikacji mobilnej, pojawią się tutaj.',
    loadError: 'Nie udało się pobrać pozycji handlowców.',
    status: {
      visit: 'Na wizycie',
      fresh: 'Aktywny',
      stale: 'Nieaktywny',
      lost: 'Brak sygnału',
      none: 'Brak lokalizacji',
    },
    ping: 'Ostatni ping: {age} temu',
    pingNever: 'Brak pingów GPS',
    ageMin: '{value} min',
    ageHour: '{h} h {m} min',
    ageNow: 'przed chwilą',
    activeVisit: 'Wizyta u klienta',
    since: 'od {time}, trwa {duration}',
    today: 'Dzisiaj',
    planned: 'plan.',
    inProgress: 'w toku',
    completed: 'zakończone',
    total: 'wizyt',
    accuracy: 'dokładność ±{value} m',
    openCustomer: 'Otwórz kartę klienta',
    flyTo: 'Pokaż na mapie',
    fitAll: 'Pokaż wszystkich',
    legend: 'Legenda',
    mapNote: 'Mapa: OpenStreetMap',
    noPosition: 'Brak pozycji GPS',
  },
} as const;

export const opsEn: Shape<typeof opsPl> = {
  nav: {
    activity: 'Rep activity',
    funnel: 'Acquisition funnel',
    bonus: 'Bonuses',
    live: 'Live map',
  },
  activity: {
    subtitle: 'Visits, portfolio coverage against contact cadence rules, GPS discipline and anomalies.',
    kpi: {
      section: 'Activity indicators',
      visits: 'Completed visits',
      coverage: 'Portfolio coverage',
      avgTime: 'Average visit length',
      perDay: '{days} working days in the period',
      gps: 'Visits with GPS',
      gpsHint: 'Share of visits started with a location',
      tasks: 'Tasks done',
      anomalies: 'Anomalies',
      anomaliesHint: 'For the manager to verify',
    },
    table: {
      title: 'Rep activity',
      subtitle: 'Click a header to sort',
      rep: 'Rep',
      visits: 'Visits',
      visitsHint: 'Completed, missed and planned visits in the period',
      missed: 'missed',
      planned: 'planned',
      perDay: 'Visits / working day',
      coverage: 'Portfolio coverage',
      avgTime: 'Avg. time',
      gps: 'GPS vs manual',
      gpsHint: '{gps} visits with GPS, {manual} entered manually',
      sales: 'Sales',
      tasks: 'Tasks',
      overdue: 'overdue',
      anomalies: 'Anomalies',
    },
    coverage: {
      title: 'Portfolio contact coverage',
      subtitle: 'Customers served within their contact cadence (visit rules)',
      onTime: 'Contacted within cadence',
      late: 'Contact overdue',
      clients: 'customers',
    },
    scatter: {
      title: 'Visits vs. sales',
      subtitle: 'Each dot is a rep in the selected period',
      visits: 'Completed visits',
      sales: 'Sales',
      trend: 'Trend',
    },
    corr: {
      strongPos: 'strong positive relationship: more visits go together with higher sales.',
      moderatePos: 'moderate positive relationship: visits explain part of the sales result.',
      weakPos: 'weak positive relationship.',
      none: 'no clear relationship between the number of visits and sales.',
      weakNeg: 'weak negative relationship.',
      moderateNeg: 'moderate negative relationship: more visits do not translate into sales.',
      strongNeg: 'strong negative relationship: check visit quality and the portfolio.',
      unavailable: 'Not enough data to compute a correlation (at least 3 reps needed).',
      caveat: 'Correlation does not prove cause: rep portfolios differ in size.',
    },
    anomalies: {
      title: 'Field work anomalies',
      subtitle: 'Visits and GPS signal that need a check',
      none: 'No anomalies in the selected period',
      more: 'Show more ({count})',
      less: 'Show less',
    },
    anomalyType: {
      GEOFENCE: 'Outside customer geofence',
      SHORT_VISIT: 'Visit too short',
      NIGHT_VISIT: 'Night visit',
      GPS_GAP: 'GPS signal gap',
    },
    anomaly: {
      GEOFENCE: 'Visit started {value} m from the customer (limit {limit} m).',
      SHORT_VISIT: 'Visit lasted {value} min (minimum {limit} min).',
      NIGHT_VISIT: 'Visit started at {value}:00 (outside {to}:00–{from}:00).',
      GPS_GAP: 'No GPS signal for {value} min (limit {limit} min).',
    },
    severity: { high: 'High', medium: 'Medium', low: 'Low' },
  },
  funnel: {
    subtitle: 'The path of leads from first contact to first order: conversion, time and source effectiveness.',
    period: { all: 'All leads', l12: '12 months', ytd: 'Year to date', quarter: 'Quarter' },
    source: 'Source',
    allSources: 'All sources',
    noSource: 'No source',
    empty: 'No leads for the selected filters',
    days: '{value} days',
    kpi: {
      section: 'Funnel indicators',
      total: 'Leads in the funnel',
      openHint: 'Open: {count}',
      openPotential: 'Open potential',
      openPotentialHint: 'Total potential of leads in progress',
      winRate: 'Win rate',
      winRateHint: 'Won: {won} · lost: {lost}',
      daysToWin: 'Time to win',
      daysToWinHint: 'On average from lead creation',
      daysToLossHint: 'Lost after {value} days on average',
      firstOrder: 'Lead → 1st order',
      firstOrderHint: 'With an order: {withOrder} · converted: {converted}',
      median: 'median {value} days',
    },
    chart: {
      title: 'Acquisition funnel',
      subtitle: 'Leads that reached each stage and conversion between stages',
      stage: 'Stage',
      reached: 'Reached the stage',
      avgDays: 'Avg. time in stage',
    },
    nowHere: 'Now: {count} · {potential}',
    conversionHint: 'Conversion from the previous stage',
    avgDaysHint: 'Average time leads spent in their current stage',
    bySource: { title: 'Source effectiveness', subtitle: 'Which acquisition channels deliver customers' },
    byRep: { title: 'Rep comparison', subtitle: 'Leads and effectiveness by owner' },
    group: {
      leads: 'Leads',
      open: 'Open',
      won: 'Won',
      lost: 'Lost',
      winRate: 'Win rate',
      wonPotential: 'Won potential',
      daysToWin: 'Days to win',
      best: 'Best',
    },
  },
  bonus: {
    subtitleSim: 'Team bonus simulation by scheme: components, adjustments and the organisation total.',
    subtitleMine: 'Your projected bonus and what you can do to raise it.',
    tab: { simulator: 'Simulator', mine: 'My bonus' },
    period: 'Month',
    scheme: 'Scheme',
    schemeAuto: 'Scheme in force for the period',
    active: 'active',
    projection: 'Projection to month end',
    projectionHint: 'Current-month results scaled to the full number of working days',
    projectionInfo: 'Projection: {done} of {total} working days (×{factor})',
    editScheme: 'Edit scheme',
    pcs: 'pcs',
    weight: 'weight {value}%',
    noTarget: 'no target',
    belowThreshold: 'below threshold',
    capped: 'cap {value}',
    thresholdMark: 'Threshold: {value}%',
    targetMark: '100% of target',
    ofBase: '{value} of base',
    kpi: {
      section: 'Bonus summary',
      total: 'Total bonuses',
      totalHint: 'Payable to the whole team',
      avg: 'Average bonus',
      avgHint: 'Per rep',
      base: 'Base bonus',
      baseHint: 'Pool at 100%: {total}',
      reps: 'Reps',
    },
    chart: { title: 'What the bonus is made of', subtitle: 'Contribution of each component to a rep’s bonus, in PLN' },
    component: { sales: 'Sales', margin: 'Margin', newCustomers: 'New customers', tasksVisits: 'Tasks and visits' },
    mandatory: {
      surveyCompleteness: 'Survey completeness',
      visitsCount: 'Number of visits',
      dataCompleteness: 'Customer data completeness',
      reportPunctuality: 'Report punctuality',
    },
    table: {
      title: 'Bonuses by rep',
      subtitle: 'Component score (% of target) and amount; expand a row for the explanation',
      rep: 'Rep',
      adjustments: 'Mandatory adjustments',
      premium: 'Bonus',
      details: 'Details',
      explanation: 'How it was calculated',
      components: 'Components: result / target',
      mandatory: 'Mandatory activity',
    },
    mine: {
      predicted: 'Projected bonus',
      result: 'Bonus for the month',
      ofBase: 'Share of base bonus',
      baseIs: '(base {value})',
      adjustments: 'Mandatory adjustments',
      progress: 'Component progress',
      progressHint: 'Thin tick: threshold, thick tick: 100% of target',
      raise: 'How to earn more',
      raiseHint: 'Sorted by the biggest gain',
      maxed: 'All components are at their maximum, great job!',
      how: 'How it is calculated',
      howHint: 'Scheme: {name}',
    },
    tip: {
      threshold: '{name}: {needed} short of the bonus threshold. Once you pass it, the component starts paying.',
      full: '{name}: {needed} short of the full target.',
      cap: '{name}: another {needed} to the component cap.',
      mandatory: '{name}: {needed} short of the required level. You avoid the penalty and earn the bonus.',
    },
    edit: {
      title: 'Edit bonus scheme',
      editing: 'Changes save scheme “{name}”',
      defaultScheme: 'Default scheme. Saving will create a new scheme in the database.',
      basic: 'Basics',
      name: 'Scheme name',
      validFrom: 'Valid from',
      validTo: 'Valid to',
      baseAmount: 'Base bonus (100% result)',
      active: 'Scheme active',
      weights: 'Component weights',
      weightsHint: 'Weights must add up to 100%.',
      weightsTotal: 'Total weight: {sum}%',
      normalize: 'Balance to 100%',
      components: 'Thresholds and caps',
      componentsHint: 'Below the threshold the component pays 0; results above the cap are clipped.',
      threshold: 'Threshold',
      thresholdHint: 'A score (% of target) below the threshold pays 0 for the component',
      cap: 'Cap',
      capHint: 'Maximum component score (% of target)',
      defaultMargin: 'Default margin target',
      newPerMonth: 'New customers / month',
      visitsPerMonth: 'Visits / month',
      taskOnTime: 'Tasks on time',
      mandatory: 'Mandatory activity',
      mandatoryHint: 'Meeting the threshold earns a bonus, missing it a penalty.',
      minimum: 'Minimum',
      bonus: 'Bonus',
      penalty: 'Penalty',
      save: 'Save changes',
      saveNew: 'Save as new',
      create: 'Create scheme',
      errors: 'Errors to fix: {count}',
      unsaved: 'Unsaved changes',
      noChanges: 'No changes',
    },
    err: {
      name: 'Enter a scheme name.',
      validFrom: 'Enter a start date.',
      validTo: 'The end date cannot be earlier than the start.',
      positive: 'Enter a number greater than zero.',
      number: 'Enter a non-negative number.',
      percent: 'Enter a value from 0 to 100.',
      weightsSum: 'Weights add up to {sum}%, but must be 100%.',
      threshold: 'The threshold must be between 0 and 100%.',
      cap: 'The cap must be between 100 and 300%.',
    },
    copySuffix: 'copy',
    saved: 'Bonus scheme saved.',
    saveError: 'Could not save the scheme.',
  },
  exports: {
    label: 'Export',
    excel: 'Excel (.xlsx)',
    pdf: 'PDF',
    preparing: 'Preparing the file…',
    done: 'Downloaded {name}',
    error: 'Could not generate the export.',
    forbidden: 'You do not have permission to export this data.',
    unavailable: 'Export is not available yet.',
    customersXlsx: 'Customer list (Excel)',
    customerPdf: 'Customer one-pager (PDF)',
    boardPdf: 'Board report (PDF)',
    repsXlsx: 'Rep table (Excel)',
    drillXlsx: 'Customer list (Excel)',
    alertsXlsx: 'Alerts (Excel)',
    tasksXlsx: 'Tasks (Excel)',
  },
  live: {
    subtitle: 'Where the reps are and who is on a visit right now. Refreshes every 30 s.',
    updated: 'Updated {time}',
    refresh: 'Refresh',
    reps: 'Reps',
    onVisit: 'on a visit',
    online: 'online',
    noGps: 'no GPS',
    search: 'Search for a rep',
    empty: 'No reps in scope',
    emptyHint: 'Reps will show up here once they start working in the mobile app.',
    loadError: 'Could not load rep positions.',
    status: {
      visit: 'On a visit',
      fresh: 'Active',
      stale: 'Inactive',
      lost: 'No signal',
      none: 'No location',
    },
    ping: 'Last ping: {age} ago',
    pingNever: 'No GPS pings',
    ageMin: '{value} min',
    ageHour: '{h} h {m} min',
    ageNow: 'just now',
    activeVisit: 'Visit at customer',
    since: 'since {time}, running {duration}',
    today: 'Today',
    planned: 'planned',
    inProgress: 'in progress',
    completed: 'completed',
    total: 'visits',
    accuracy: 'accuracy ±{value} m',
    openCustomer: 'Open customer card',
    flyTo: 'Show on map',
    fitAll: 'Show everyone',
    legend: 'Legend',
    mapNote: 'Map: OpenStreetMap',
    noPosition: 'No GPS position',
  },
};
